import { useTranslation } from "react-i18next";
import { Controller, useFormContext } from "react-hook-form";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Box, Typography, FormControl, FormLabel, FormHelperText } from "@mui/material";

import { CreateContractData } from "../../../types/service.type";

import { CalendarIcon } from "../../../assets/icons/go-hire-icons";

const ServicePaymentDueDate = () => {
  const { t } = useTranslation("goService", { keyPrefix: "createContractPage" });

  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext<CreateContractData>();
  return (
    <Box sx={styles.container}>
      <Box sx={styles.titleContainer}>
        <Typography sx={styles.title}>{t("paymentSchedule")}</Typography>
        <Typography sx={styles.description}>
          {t("setTheFinalPaymentDateAndTimeTheFullAmountWillBePaidInOnePayment")}
        </Typography>
      </Box>
      <FormControl
        fullWidth
        component="fieldset"
        sx={styles.formControl}
        error={!!errors.paymentConfig?.paymentDueDate}
      >
        <FormLabel sx={styles.formLabel}>{t("servicePaymentDateAndTime")}</FormLabel>
        <Controller
          name="paymentConfig.paymentDueDate"
          control={control}
          render={({ field }) => (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                {...field}
                value={field.value ? new Date(field.value) : null}
                onChange={(date) => {
                  field.onChange(date?.toISOString());
                  setValue(`milestones.${0}.paymentDueDate`, date?.toISOString());
                }}
                views={["year", "month", "day", "hours", "minutes"]}
                disablePast
                slotProps={{
                  textField: {
                    error: !!errors.paymentConfig?.paymentDueDate,
                    sx: styles.textField,
                  },
                }}
                slots={{
                  openPickerIcon: CalendarIcon,
                }}
              />
            </LocalizationProvider>
          )}
        />
        {errors.paymentConfig?.paymentDueDate && (
          <FormHelperText error>{t(errors.paymentConfig?.paymentDueDate?.message)}</FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

export default ServicePaymentDueDate;

const styles = {
  container: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  formControl: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  formLabel: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  textField: {
    "& .MuiInputBase-root.MuiOutlinedInput-root": {
      borderRadius: "8px",
      border: "none",
      background: "var(--Gray---50, #F5F6F5)",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  title: {
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  description: {
    color: "var(--Gray-600, #475467)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
  },
};
